import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {availableTagEntries} from '../helpers/availableTagEntries';
import {getTagIcon} from '../helpers/tagIcons';
import {uploadMedia, deleteMedia} from '../helpers/localApi';
import {
  generateSentences,
  suggestTag,
  suggestTranslation,
  textToSpeech,
  generateImage,
} from '../helpers/externalApi';
import {useAiAvailable} from '../contexts/AiAvailability';
import {MiniKeyboard} from './MiniKeyboard';

const MASTERY_MASTERED = 4;

// Status bucket label for the drawer progress block.
function wordProgressLabel(progress) {
  const level = progress && progress.masteryLevel ? progress.masteryLevel : 0;
  if (level >= MASTERY_MASTERED) return 'Mastered';
  if (level >= 3) return 'Review';
  if (level >= 2) return 'Familiar';
  if (level >= 1) return 'Learning';
  return 'New';
}

// Human-friendly next-due date ("today", "tomorrow", "3 days", or "—").
function formatDueDate(dueAt) {
  if (!dueAt) return '—';
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return '—';
  const diffMs = due.getTime() - Date.now();
  if (diffMs <= 0) return 'now';
  const days = Math.ceil(diffMs / 86400000);
  if (days <= 1) return 'tomorrow';
  return `${days} days`;
}

export const WordDetailsDrawer = (props) => {
  const mediaAudioRef = React.useRef(null);
  const swipeStartXRef = React.useRef(null);
  const swipeStartYRef = React.useRef(null);
  const {aiAvailable} = useAiAvailable();
  const {
    open,
    onClose,
    selectedWord,
    progress,
    hasNextWord,
    hasPreviousWord,
    onShowNextWord,
    onShowPreviousWord,
    language,
    editWord,
    deleteWord,
    addCategory,
    deleteCategory,
  } = props;

  const [wordEn, setWordEn] = useState('');
  const [wordFa, setWordFa] = useState('');
  const [transliteration, setTransliteration] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFlash, setSaveFlash] = useState(false);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // On-screen Hebrew/Farsi keyboard for the target-language field.
  const wordFaInputRef = useRef(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const keyboardDismissedRef = useRef(false);

  // AI enrichment state
  const [sentences, setSentences] = useState([]);
  const [generatingSentences, setGeneratingSentences] = useState(false);
  const [autoTagging, setAutoTagging] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsAudioUrl, setTtsAudioUrl] = useState('');
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestSourceLoading, setSuggestSourceLoading] = useState(false);
  const [suggestTranslitLoading, setSuggestTranslitLoading] = useState(false);

  useEffect(() => {
    if (!selectedWord) {
      setWordEn('');
      setWordFa('');
      setTransliteration('');
      setMediaUrl('');
      setIsDirty(false);
      setConfirmDeleteOpen(false);
      setSentences([]);
      setTtsAudioUrl('');
      return;
    }

    setWordEn(selectedWord.wordEn || '');
    setWordFa(selectedWord.wordFa || '');
    setTransliteration(selectedWord.transliteration || '');
    setMediaUrl(selectedWord.mediaUrl || '');
    setIsDirty(false);
    setConfirmDeleteOpen(false);
    setTtsAudioUrl('');

    // Parse saved sentences
    try {
      const saved = selectedWord.sentences
        ? JSON.parse(selectedWord.sentences)
        : [];
      setSentences(Array.isArray(saved) ? saved : []);
    } catch (_) {
      setSentences([]);
    }
  }, [selectedWord]);

  useEffect(() => {
    if (!open) {
      swipeStartXRef.current = null;
      setDragOffsetX(0);
      setIsDragging(false);
    }
  }, [open]);

  // Close on Escape; cancel an open delete confirmation first.
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (confirmDeleteOpen) {
        setConfirmDeleteOpen(false);
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, confirmDeleteOpen, onClose]);

  const availableTagsToAdd = useMemo(() => {
    const currentTags = Array.isArray(selectedWord && selectedWord.tags)
      ? selectedWord.tags : [];
    return availableTagEntries
        .filter((tag) => tag !== 'Untagged')
        .filter((tag) => currentTags.indexOf(tag) === -1);
  }, [selectedWord]);

  const toggleKeyboard = useCallback(() => {
    setShowKeyboard((prev) => {
      const next = !prev;
      keyboardDismissedRef.current = !next;
      if (next && wordFaInputRef.current) {
        setTimeout(() => wordFaInputRef.current.focus(), 0);
      }
      return next;
    });
  }, []);

  const handleKeyboardClose = useCallback(() => {
    keyboardDismissedRef.current = true;
    setShowKeyboard(false);
  }, []);

  const handleWordFaFocus = useCallback(() => {
    if (!keyboardDismissedRef.current) setShowKeyboard(true);
  }, []);

  if (!open || !selectedWord) {
    return null;
  }

  const langLabel =
    language === 'hebrew' ? 'Hebrew' : 'Farsi';

  let aiStatusText = '';
  if (suggestLoading) aiStatusText = `Generating ${langLabel} translation...`;
  else if (suggestSourceLoading) aiStatusText = 'Generating English translation...';
  else if (suggestTranslitLoading) aiStatusText = 'Generating transliteration...';
  else if (generatingSentences) aiStatusText = 'Generating sample sentences...';
  else if (autoTagging) aiStatusText = 'Generating tag suggestion...';
  else if (ttsLoading) aiStatusText = 'Generating pronunciation audio...';
  else if (imageGenLoading) aiStatusText = 'Generating image... this may take up to 30s';

  const handleFieldChange = (setter) => (event) => {
    setter(event.target.value);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (isSaving) return;

    const cleanedWordEn = wordEn
      ? wordEn.charAt(0).toUpperCase() + wordEn.slice(1)
      : wordEn;
    const cleanedWordFa = wordFa
      ? wordFa.charAt(0).toUpperCase() + wordFa.slice(1)
      : wordFa;

    setIsSaving(true);
    try {
      await editWord(
          selectedWord.key,
          cleanedWordEn,
          cleanedWordFa,
          mediaUrl,
          transliteration,
          JSON.stringify(sentences),
      );
      setIsDirty(false);
      setSaveFlash(true);
      onClose();
      setTimeout(() => setSaveFlash(false), 1500);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteWord = () => {
    deleteWord(selectedWord.key);
    setConfirmDeleteOpen(false);
    onClose();
  };

  const handleAddTag = (event) => {
    const newTag = event.target.value;
    setSelectedTagToAdd(newTag);
    if (newTag) {
      addCategory(selectedWord.key, newTag);
      setSelectedTagToAdd('');
    }
  };

  const handleUploadFile = async (event) => {
    const file =
      event.target.files && event.target.files[0];
    if (!file) return;

    setIsUploading(true);

    try {
      const response = await uploadMedia({language, file});
      setMediaUrl(response.mediaUrl);
      setIsDirty(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!mediaUrl) return;

    try {
      await deleteMedia({mediaUrl});
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    setMediaUrl('');
    setIsDirty(true);
  };

  // ── AI: suggest translation fill ──────────────────
  const handleSuggestTranslation = async () => {
    if (!wordEn.trim()) return;
    setSuggestLoading(true);
    try {
      const result = await suggestTranslation({
        word: wordEn,
        fromLang: 'English',
        toLang: langLabel,
      });
      if (result.translation) {
        setWordFa(result.translation);
        setIsDirty(true);
      }
      if (result.transliteration) {
        setTransliteration(result.transliteration);
        setIsDirty(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Suggest failed:', err.message);
    } finally {
      setSuggestLoading(false);
    }
  };

  const handleSuggestSourceFromTarget = async () => {
    if (!wordFa.trim()) return;
    setSuggestSourceLoading(true);
    try {
      const result = await suggestTranslation({
        word: wordFa,
        fromLang: langLabel,
        toLang: 'English',
      });
      if (result.translation) {
        setWordEn(result.translation);
        setIsDirty(true);
      }
      if (result.transliteration && !transliteration.trim()) {
        setTransliteration(result.transliteration);
        setIsDirty(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Suggest source failed:', err.message);
    } finally {
      setSuggestSourceLoading(false);
    }
  };

  const handleSuggestTransliteration = async () => {
    if (!wordFa.trim()) return;
    setSuggestTranslitLoading(true);
    try {
      const result = await suggestTranslation({
        word: wordFa,
        fromLang: langLabel,
        toLang: 'English',
      });
      if (result.transliteration) {
        setTransliteration(result.transliteration);
        setIsDirty(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Suggest transliteration failed:', err.message);
    } finally {
      setSuggestTranslitLoading(false);
    }
  };

  // ── AI: generate sample sentences ──────────────────
  const handleGenerateSentences = async () => {
    if (!wordEn && !wordFa) return;
    setGeneratingSentences(true);
    try {
      const result = await generateSentences({
        wordEn: wordEn || '',
        wordFa: wordFa || '',
        language,
      });
      setSentences(result);
      setIsDirty(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Sentence generation failed:', err.message);
    } finally {
      setGeneratingSentences(false);
    }
  };

  // ── Save sentences to word ────────────────────────
  const handleSaveSentences = async () => {
    if (!selectedWord || sentences.length === 0) return;
    try {
      await editWord(
          selectedWord.key,
          wordEn,
          wordFa,
          mediaUrl,
          transliteration,
          JSON.stringify(sentences),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Save sentences failed:', err.message);
    }
  };

  // ── AI: auto-tag ──────────────────────────────────
  const handleAutoTag = async () => {
    if (!wordEn) return;
    setAutoTagging(true);
    try {
      const tag = await suggestTag({
        wordEn,
        wordFa,
      });
      addCategory(selectedWord.key, tag);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Auto-tag failed:', err.message);
    } finally {
      setAutoTagging(false);
    }
  };

  // ── AI: TTS pronunciation ─────────────────────────
  const handleTts = async () => {
    const text = wordFa || wordEn;
    if (!text) return;
    setTtsLoading(true);
    try {
      const blob = await textToSpeech({text});
      const url = URL.createObjectURL(blob);
      setTtsAudioUrl(url);
      const audio = new Audio(url);
      audio.play();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('TTS failed:', err.message);
    } finally {
      setTtsLoading(false);
    }
  };

  // ── AI: save TTS as word media ────────────────────
  const handleSaveTtsAsMedia = async () => {
    if (!ttsAudioUrl) return;
    setIsUploading(true);
    try {
      const resp = await fetch(ttsAudioUrl);
      const blob = await resp.blob();
      const file = new File([blob], 'tts-audio.wav', {
        type: 'audio/wav',
      });
      const uploadResp = await uploadMedia({
        language,
        file,
      });
      setMediaUrl(uploadResp.mediaUrl);
      setIsDirty(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Save TTS failed:', err.message);
    } finally {
      setIsUploading(false);
    }
  };

  // ── AI: image generation ──────────────────────────
  const handleGenerateImage = async () => {
    const concept = wordEn || wordFa;
    if (!concept) return;
    setImageGenLoading(true);
    try {
      const prompt =
        `A simple, clean illustration of the concept: ${concept}`;
      const result = await generateImage({prompt});
      if (result.imageUrl || result.image) {
        const imgUrl = result.imageUrl || result.image;
        setMediaUrl(imgUrl);
        setIsDirty(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Image gen failed:', err.message);
    } finally {
      setImageGenLoading(false);
    }
  };

  const currentTags = Array.isArray(selectedWord.tags)
    ? selectedWord.tags : [];

  const isInteractiveTarget = (target) => {
    if (!target || typeof target.closest !== 'function') {
      return false;
    }

    return Boolean(
        target.closest('input, textarea, select, button, a, audio, label'),
    );
  };

  const handlePanelTouchStart = (event) => {
    if (!event.touches || event.touches.length !== 1) {
      return;
    }

    if (isInteractiveTarget(event.target)) {
      return;
    }

    swipeStartXRef.current = event.touches[0].clientX;
    swipeStartYRef.current = event.touches[0].clientY;
    setIsDragging(true);
  };

  const handlePanelTouchMove = (event) => {
    if (
      swipeStartXRef.current === null ||
      !event.touches ||
      event.touches.length !== 1
    ) {
      return;
    }

    const deltaX =
      event.touches[0].clientX - swipeStartXRef.current;
    const deltaY =
      event.touches[0].clientY - swipeStartYRef.current;

    // Only track clearly horizontal gestures (same ratio as StudyMode).
    // This prevents conflicting with vertical scroll in the drawer body.
    if (Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      setDragOffsetX(
        Math.sign(deltaX) * Math.min(Math.abs(deltaX), 160),
      );
    }
  };

  const endDrag = () => {
    const shouldTrigger = Math.abs(dragOffsetX) > 64;
    swipeStartXRef.current = null;
    swipeStartYRef.current = null;
    setIsDragging(false);

    if (shouldTrigger) {
      if (dragOffsetX < 0 && hasNextWord) {
        onShowNextWord();
      } else if (dragOffsetX > 0 && hasPreviousWord) {
        onShowPreviousWord();
      } else if (dragOffsetX > 0) {
        onClose();
      }
    }

    setDragOffsetX(0);
  };

  return (
    <div className="drawer-wrap">
      <div className="drawer-backdrop" onClick={onClose} />
      <div
        className="drawer-panel"
        style={{
          transform: `translateX(${dragOffsetX}px)`,
          transition: isDragging ? 'none' : undefined,
        }}
        onTouchStart={handlePanelTouchStart}
        onTouchMove={handlePanelTouchMove}
        onTouchEnd={endDrag}
        onTouchCancel={endDrag}
      >
        <div className="drawer-swipe-handle-wrap" aria-hidden="true">
          <span className="drawer-swipe-handle" />
        </div>
        {/* Header */}
        <div className="drawer-header">
          <h2 className="drawer-title">Edit Word</h2>
          <span className="drawer-mobile-hint">Swipe left or right</span>
          <button
            type="button"
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="drawer-swipe-tip" aria-live="polite">
          <span>← Next</span>
          <span>Previous →</span>
        </div>

        <div className="drawer-body">
          {aiStatusText && (
            <div className="ai-progress-banner" role="status" aria-live="polite">
              <span className="ai-progress-dot" aria-hidden="true" />
              <span>{aiStatusText}</span>
            </div>
          )}

          {/* Word fields section */}
          <section className="drawer-section">
            <div className="row-between">
              <h3 className="drawer-section-title">
                Translation
              </h3>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label className="form-label">
                  English
                </label>
                <div className="row">
                  <input
                    className="field"
                    placeholder="English word or phrase"
                    value={wordEn}
                    onChange={handleFieldChange(setWordEn)}
                    style={{flex: 1}}
                  />
                  {aiAvailable && (
                    <button
                      type="button"
                      className="btn btn-sm btn-ai"
                      onClick={handleSuggestTranslation}
                      disabled={suggestLoading || !wordEn.trim()}
                      title="AI auto-fill translation"
                    >
                      {suggestLoading ? '⏳' : '✨'} Suggest
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {langLabel}
                </label>
                <div className="row">
                  <input
                    className={`field ${language === 'farsi' ? 'font-farsi' : 'font-hebrew'}`}
                    placeholder={`${langLabel} translation`}
                    dir="rtl"
                    value={wordFa}
                    onChange={handleFieldChange(setWordFa)}
                    onFocus={handleWordFaFocus}
                    ref={wordFaInputRef}
                    style={{flex: 1}}
                  />
                  <button
                    type="button"
                    className={`btn btn-sm mini-keyboard-toggle${showKeyboard ? ' btn-mic-active' : ''}`}
                    onClick={toggleKeyboard}
                    title={`Show ${langLabel} on-screen keyboard`}
                    aria-pressed={showKeyboard}
                  >
                    ⌨
                  </button>
                  {aiAvailable && (
                    <button
                      type="button"
                      className="btn btn-sm btn-ai"
                      onClick={handleSuggestSourceFromTarget}
                      disabled={suggestSourceLoading || !wordFa.trim()}
                      title="AI fill English from target language"
                    >
                      {suggestSourceLoading ? '⏳' : '✨'} To EN
                    </button>
                  )}
                </div>
                {showKeyboard && (
                  <MiniKeyboard
                    language={language}
                    inputRef={wordFaInputRef}
                    onClose={handleKeyboardClose}
                  />
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Transliteration
                </label>
                <div className="row">
                  <input
                    className="field"
                    placeholder="Phonetic spelling"
                    value={transliteration}
                    onChange={handleFieldChange(
                      setTransliteration,
                    )}
                    style={{flex: 1}}
                  />
                  {aiAvailable && (
                    <button
                      type="button"
                      className="btn btn-sm btn-ai"
                      onClick={handleSuggestTransliteration}
                      disabled={suggestTranslitLoading || !wordFa.trim()}
                      title="AI transliteration from target language"
                    >
                      {suggestTranslitLoading ? '⏳' : '✨'} Translit
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>

          {aiAvailable && (
            <section className="drawer-section">
              <div className="row-between">
                <h3 className="drawer-section-title">
                  AI Tools
                </h3>
              </div>
              <div className="ai-actions-row">
                <button
                  type="button"
                  className="btn btn-sm btn-ai"
                  onClick={handleAutoTag}
                  disabled={autoTagging}
                >
                  {autoTagging ? '⏳' : '🏷️'} Auto-Tag
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ai"
                  onClick={handleTts}
                  disabled={ttsLoading}
                >
                  {ttsLoading ? '⏳' : '🔊'} Pronounce
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ai"
                  onClick={handleGenerateImage}
                  disabled={imageGenLoading}
                >
                  {imageGenLoading ? '⏳' : '🖼️'} Image
                </button>
              </div>
              {ttsAudioUrl && (
                <div className="ai-result-card">
                  <audio
                    controls
                    src={ttsAudioUrl}
                    style={{width: '100%', marginTop: 8}}
                  />
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={handleSaveTtsAsMedia}
                    disabled={isUploading}
                    style={{marginTop: 6}}
                  >
                    <span role="img" aria-label="save">💾</span> Save as word audio
                  </button>
                </div>
              )}
            </section>
          )}

          {/* Progress section */}
          {progress && (
            <section className="drawer-section">
              <h3 className="drawer-section-title">Progress</h3>
              <div className="word-progress-grid">
                <div className="word-progress-item">
                  <span className="word-progress-value">{wordProgressLabel(progress)}</span>
                  <span className="word-progress-label">Status</span>
                </div>
                <div className="word-progress-item">
                  <span className="word-progress-value">
                    {progress.correctCount + progress.incorrectCount > 0
                      ? `${Math.round((100 * progress.correctCount) / (progress.correctCount + progress.incorrectCount))}%`
                      : '—'}
                  </span>
                  <span className="word-progress-label">Correct</span>
                </div>
                <div className="word-progress-item">
                  <span className="word-progress-value">
                    {progress.correctCount + progress.incorrectCount}
                  </span>
                  <span className="word-progress-label">Reviews</span>
                </div>
                <div className="word-progress-item">
                  <span className="word-progress-value">
                    {formatDueDate(progress.dueAt)}
                  </span>
                  <span className="word-progress-label">Next due</span>
                </div>
              </div>
              {progress.lapses >= 4 && (
                <p className="drawer-empty-text word-progress-leech">
                  <span role="img" aria-label="warning">⚠️</span> Trouble word — {progress.lapses} lapses. Consider a mnemonic or extra practice.
                </p>
              )}
              {selectedWord.sourceUrl && (
                <p className="word-source-line">
                  Source:{' '}
                  <a href={selectedWord.sourceUrl} target="_blank" rel="noopener noreferrer" dir="ltr">
                    {selectedWord.sourceTitle || selectedWord.sourceUrl}
                  </a>
                </p>
              )}
            </section>
          )}

          {/* Sample Sentences section */}
          <section className="drawer-section">
            <div className="row-between">
              <h3 className="drawer-section-title">
                Sample Sentences
              </h3>
              {aiAvailable && (
                <button
                  type="button"
                  className="btn btn-sm btn-ai"
                  onClick={handleGenerateSentences}
                  disabled={generatingSentences || (!wordEn && !wordFa)}
                >
                  {generatingSentences ? '⏳' : '✨'} Generate
                </button>
              )}
            </div>
            {sentences.length > 0 ? (
              <div className="sentences-list">
                {sentences.map((s, i) => (
                  <div key={i} className="sentence-card">
                    <div className="sentence-text">{s.text}</div>
                    {s.transliteration && (
                      <div className="sentence-translit">{s.transliteration}</div>
                    )}
                    <div className="sentence-translation">{s.translation}</div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleSaveSentences}
                  style={{marginTop: 6}}
                >
                  <span role="img" aria-label="save">💾</span> Save Sentences
                </button>
              </div>
            ) : (
              <p className="drawer-empty-text">
                No sample sentences yet
              </p>
            )}
          </section>

          {/* Tags section */}
          <section className="drawer-section">
            <h3 className="drawer-section-title">Tags</h3>
            {currentTags.length > 0 ? (
              <div className="chips">
                {currentTags.map((tag) => (
                  <span key={tag} className="chip">
                    {getTagIcon(tag)} {tag}
                    <button
                      type="button"
                      onClick={() =>
                        deleteCategory(
                          selectedWord.key,
                          tag,
                        )
                      }
                      aria-label={`Remove ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="drawer-empty-text">
                No tags assigned
              </p>
            )}
            {availableTagsToAdd.length > 0 && (
              <select
                className="field field-sm"
                value={selectedTagToAdd}
                onChange={handleAddTag}
                style={{marginTop: '10px'}}
              >
                <option value="">+ Add Tag</option>
                {availableTagsToAdd.map((tag) => (
                  <option key={tag} value={tag}>
                    {getTagIcon(tag)} {tag}
                  </option>
                ))}
              </select>
            )}
          </section>

          {/* Media section */}
          <section className="drawer-section">
            <h3 className="drawer-section-title">Media</h3>
            {mediaUrl ? (
              <div className="media-preview">
                {mediaUrl.includes('.m4a') ||
                mediaUrl.includes('.mp3') ||
                mediaUrl.includes('.wav') ? (
                  <div className="media-preview-item">
                    <span className="media-icon media-icon-audio">
                      ♫
                    </span>
                    <span className="media-filename">
                      Audio file
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => mediaAudioRef.current && mediaAudioRef.current.play()}
                    >
                      Play
                    </button>
                    <audio ref={mediaAudioRef} src={mediaUrl} />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleDeleteFile}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="media-preview-item">
                    <span className="media-icon media-icon-image">
                      🖼
                    </span>
                    <span className="media-filename">
                      Image file
                    </span>
                    <a
                      href={mediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm"
                    >
                      View
                    </a>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleDeleteFile}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="drawer-empty-text">
                No media attached
              </p>
            )}
            <label className="file-upload-label">
              <input
                type="file"
                className="file-upload-input"
                onChange={handleUploadFile}
              />
              <span
                className="btn btn-sm"
                style={{marginTop: '10px'}}
              >
                {isUploading
                  ? 'Uploading...'
                  : '📎 Upload File'}
              </span>
            </label>
          </section>
        </div>

        {/* Footer actions */}
        <div className="drawer-footer">
          {!confirmDeleteOpen ? (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete Word
            </button>
          ) : (
            <div className="confirm-delete">
              <span className="confirm-delete-text">
                Delete this word?
              </span>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setConfirmDeleteOpen(false)}
              >
                No
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger-fill"
                onClick={handleDeleteWord}
              >
                Yes, Delete
              </button>
            </div>
          )}
          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className={`btn btn-primary${saveFlash ? ' btn-save-flash' : ''}`}
              onClick={handleSave}
              disabled={!isDirty || isSaving}
            >
              {isSaving ? 'Saving...' : saveFlash ? '✓ Saved' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
